import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Search, Loader2 } from "lucide-react";

const DIFF_STYLE = {
  basico: "bg-emerald-50 text-emerald-700 border-emerald-200",
  intermedio: "bg-amber-50 text-amber-700 border-amber-200",
  avanzado: "bg-rose-50 text-rose-700 border-rose-200",
};

// La escuela enseña la 3ª persona como "usted / ustedes", no "él / ellos".
const PERSON_HEAD = {
  yo: "yo", tu: "tú", vos: "vos", el: "usted",
  nosotros: "nosotros", vosotros: "vosotros", ellos: "ustedes",
};

// Un color por pronombre y otro por tiempo: ayuda a fijar la forma visualmente.
const PERSON_STYLE = {
  yo: "text-sky-700 bg-sky-50/60",
  tu: "text-violet-700 bg-violet-50/60",
  vos: "text-fuchsia-700 bg-fuchsia-50/60",
  el: "text-amber-700 bg-amber-50/60",
  nosotros: "text-emerald-700 bg-emerald-50/60",
  vosotros: "text-teal-700 bg-teal-50/60",
  ellos: "text-rose-700 bg-rose-50/60",
};

const TENSE_STYLE = {
  presente: "border-l-sky-400 text-sky-800 bg-sky-50/40",
  preterito: "border-l-amber-400 text-amber-800 bg-amber-50/40",
  imperfecto: "border-l-orange-400 text-orange-800 bg-orange-50/40",
  futuro: "border-l-emerald-400 text-emerald-800 bg-emerald-50/40",
  condicional: "border-l-teal-400 text-teal-800 bg-teal-50/40",
  subjuntivo_presente: "border-l-violet-400 text-violet-800 bg-violet-50/40",
  subjuntivo_imperfecto: "border-l-fuchsia-400 text-fuchsia-800 bg-fuchsia-50/40",
  imperativo_afirmativo: "border-l-emerald-400 text-emerald-800 bg-emerald-50/40",
  imperativo_negativo: "border-l-rose-400 text-rose-800 bg-rose-50/40",
};

export const ConjugationDictionary = ({ showVos, showVosotros, onTogglePerson }) => {
  const { t, lang } = useI18n();
  const [data, setData] = useState({ groups: [], total: 0 });
  const [q, setQ] = useState("");
  const [difficulty, setDifficulty] = useState("all");
  const [open, setOpen] = useState(null);
  const [table, setTable] = useState(null);

  useEffect(() => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (difficulty !== "all") p.set("difficulty", difficulty);
    const t = setTimeout(() => {
      api.get(`/conjugation/dictionary?${p.toString()}`).then((r) => setData(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q, difficulty]);

  const openVerb = async (verb) => {
    setOpen(verb);
    setTable(null);
    const r = await api.get(`/conjugation/${encodeURIComponent(verb.infinitive)}`);
    setTable(r.data);
  };

  const persons = useMemo(() => {
    if (!table) return [];
    return (table.persons || []).filter(
      (p) => (p !== "vos" || showVos) && (p !== "vosotros" || showVosotros));
  }, [table, showVos, showVosotros]);

  const impPersons = useMemo(() => {
    if (!table) return [];
    return (table.imperative_persons || []).filter(
      (p) => (p !== "vos" || showVos) && (p !== "vosotros" || showVosotros));
  }, [table, showVos, showVosotros]);

  const simple = table ? Object.keys(table.tenses).filter((t) => !t.startsWith("imperativo")) : [];
  const imperativos = table ? Object.keys(table.tenses).filter((t) => t.startsWith("imperativo")) : [];

  const renderVerb = (v) => (
    <button
      key={v.infinitive}
      onClick={() => openVerb(v)}
      data-testid={`dictionary-verb-${v.infinitive.replace(/\s/g, "-")}`}
      className={`text-left border rounded-lg px-3 py-2 transition-all hover:-translate-y-0.5 hover:shadow-md ${DIFF_STYLE[v.difficulty] || "bg-white"}`}
    >
      <span className="block font-medium text-slate-900">{v.infinitive}</span>
      <span className="block text-[11px] text-slate-500">{v.translation}</span>
    </button>
  );

  return (
    <div className="space-y-5" data-testid="conjugation-dictionary">
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white"
                 placeholder={t("cj_search_verb")}
                 data-testid="dictionary-search" />
        </div>
        <Select value={difficulty} onValueChange={setDifficulty}>
          <SelectTrigger className="w-[180px] bg-white" data-testid="dictionary-difficulty">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("cj_difficulty_any")}</SelectItem>
            <SelectItem value="basico">{t("cj_diff_basico")}</SelectItem>
            <SelectItem value="intermedio">{t("cj_diff_intermedio")}</SelectItem>
            <SelectItem value="avanzado">{t("cj_diff_avanzado")}</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-500" data-testid="dictionary-total">
          {data.total} {t("cj_verbs_count")}
        </span>
      </div>

      {data.groups.map((g) => (
        <Card key={g.group} className="p-5" data-testid={`dictionary-group-${g.group}`}>
          <div className="flex items-baseline gap-3 mb-3">
            <h3 className="text-lg font-semibold text-slate-900">{lang === "en" ? (g.label_en || g.label) : g.label}</h3>
            <span className="text-xs text-slate-400">{g.count}</span>
          </div>
          {g.subgroups?.length ? (
            <div className="space-y-4">
              {g.subgroups.map((s) => (
                <div key={s.ending} data-testid={`dictionary-subgroup-${s.ending}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold tracking-widest text-emerald-700 bg-emerald-50
                                     border border-emerald-200 rounded px-2 py-0.5">{s.label}</span>
                    <span className="text-xs text-slate-400">{s.count}</span>
                    <span className="flex-1 h-px bg-slate-100" />
                  </div>
                  <div className="flex flex-wrap gap-2">{s.verbs.map(renderVerb)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">{g.verbs.map(renderVerb)}</div>
          )}
        </Card>
      ))}

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {open?.infinitive}
              <span className="text-sm font-normal text-slate-500">{open?.translation}</span>
              {open && (
                <Badge variant="outline" className={DIFF_STYLE[open.difficulty]}>
                  {t(`cj_diff_${open.difficulty}`)}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {!table ? (
            <div className="py-10 text-center text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin mx-auto" />
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center gap-6 text-sm bg-slate-50 rounded-lg p-3">
                <span><strong>{t("cj_gerund")}:</strong> {table.non_finite.gerundio}</span>
                <span><strong>{t("cj_participle")}:</strong> {table.non_finite.participio}</span>
                <div className="flex items-center gap-2 ml-auto">
                  <Label htmlFor="d-vos" className="text-xs">{t("cj_include_vos")}</Label>
                  <Switch id="d-vos" checked={showVos} data-testid="dictionary-vos-switch"
                          onCheckedChange={(v) => onTogglePerson("vos", v)} />
                  <Label htmlFor="d-vosotros" className="text-xs ml-3">{t("cj_include_vosotros")}</Label>
                  <Switch id="d-vosotros" checked={showVosotros} data-testid="dictionary-vosotros-switch"
                          onCheckedChange={(v) => onTogglePerson("vosotros", v)} />
                </div>
              </div>
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm" data-testid="conjugation-table">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left py-2.5 pl-4 pr-4 text-xs uppercase tracking-wide text-slate-500">{t("cj_tense_col")}</th>
                      {persons.map((p) => (
                        <th key={p} className={`text-left py-2.5 px-3 text-xs uppercase tracking-wide font-semibold ${PERSON_STYLE[p] || "text-slate-500"}`}>{PERSON_HEAD[p] || p}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {simple.map((tn) => (
                      <tr key={tn} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/70 transition-colors">
                        <td className={`py-2.5 pl-3 pr-4 font-semibold whitespace-nowrap border-l-4 ${TENSE_STYLE[tn] || "border-l-slate-200 text-slate-700"}`}>{t(`cj_t_${tn}`)}</td>
                        {persons.map((p) => (
                          <td key={p} className="py-2.5 px-3 text-slate-700">{table.tenses[tn][p]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left py-2.5 pl-4 pr-4 text-xs uppercase tracking-wide text-slate-500">{t("cj_imperative_col")}</th>
                      {impPersons.map((p) => (
                        <th key={p} className={`text-left py-2.5 px-3 text-xs uppercase tracking-wide font-semibold ${PERSON_STYLE[p] || "text-slate-500"}`}>{PERSON_HEAD[p] || p}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {imperativos.map((tn) => (
                      <tr key={tn} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/70 transition-colors">
                        <td className={`py-2.5 pl-3 pr-4 font-semibold whitespace-nowrap border-l-4 ${TENSE_STYLE[tn] || "border-l-slate-200 text-slate-700"}`}>
                          {tn.includes("negativo") ? t("cj_negative") : t("cj_affirmative")}
                        </td>
                        {impPersons.map((p) => (
                          <td key={p} className="py-2.5 px-3 text-slate-700">{table.tenses[tn][p] || "—"}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
